import React from "react";
import useMediaQuery from "@/utils/hooks/useMediaQuery";
import { Select } from "@dxp-feed/ui/Select";
import { Text } from "@dxp-feed/ui/Text";
import { Button } from "@dxp-feed/ui/Button";
import { Radio } from "@dxp-feed/ui/Radio";
import { SideBarSortProps } from "@/utils/types/recipes";
import { Box } from "@dxp-feed/ui/Box";
import {
  DEFAULT_SEARCH_PAGE_SORTING,
  DEFAULT_SORT_LABEL,
  GLOBAL_SEARCH_RESULTS_SECTIONS,
  SEARCH_RESULT_TAB_QUERY_PARAM_KEY,
  SHORTING_ARTICLE,
  SHORTING_PRODUCTS,
  SHORTING_RECIPES,
} from "@/utils/constant";
import { useHits, useSortBy } from "@dxp-feed/algolia";

import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { setSortDefault } from "@/utils/redux/recipes/recipeSlice";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { algoliaConfig } from "@/services/algolia/config";
import { setFilters } from "@/utils/redux/productsListing";
import { setFilterData } from "@/utils/redux/fetchOffers";
import { setFavouriteMessage } from "@/utils/redux/favouriteProduct";
import { setShoppingMessage } from "@/utils/redux/shoppingList";
import { normalizeString } from "@/helpers/global";

type SortProps = SideBarSortProps & {
  closeSort: () => void;
  type: any; // Restricts `type` to valid keys
  sortClear?: boolean;
  setSortOption?: (value: string) => void;
  displayedOffers?: any;
};

const Sort: React.FC<SortProps> = ({
  isSortVisible = false,
  closeSort,
  type,
  sortClear,
  setSortOption,
  displayedOffers,
}) => {
  const pathname = usePathname();
  const { aemFilterData, sortOptionLabel }: any = useAppSelector(
    (state) => state.getAemDataSlice
  );
  const dispatch = useAppDispatch();
  const { items: hitsItems } = useHits();

  const ArticlesortData = useAppSelector(
    (state) => state?.AemArticledataSlice?.sideBar?.sortingOptions
  );

  const currentOptions = {
    recipes: SHORTING_RECIPES.map((option: any, i: number) => ({
      ...option,
      label: aemFilterData?.["sortingOptions"]?.[i] ?? option.label,
    })),
    articles: SHORTING_ARTICLE.map((option: any, i: number) => ({
      ...option,
      label: ArticlesortData?.["sortingOptions"]?.[i] ?? option.label,
    })),
    products: SHORTING_PRODUCTS,
  } as any;

  const options =
    currentOptions[type]
      ?.map((option: any) => {
        if (pathname === "/" || option?.label !== DEFAULT_SEARCH_PAGE_SORTING) {
          return {
            optionname: option?.label,
            optionval: option?.value,
          };
        }
        return null;
      })
      .filter(Boolean) || [];

  const windowWidth = useMediaQuery(); // Get the window width
  const mediaScreen = (windowWidth ?? 0) <= 1023; // Check if the window width is less than or equal to 1023px
  const { refine, currentRefinement } = useSortBy({
    items: currentOptions[type],
  }); // Get the options and refine function from the useSortBy hook of ALogolia

  const defaultSortKey = SHORTING_PRODUCTS.find(
    (item) => item?.label === DEFAULT_SEARCH_PAGE_SORTING
  );

  useEffect(() => {
    if (type == "products" && sortClear) {
      refine(defaultSortKey?.value ?? "");
      setSelectedSort("");
    }
  }, [sortClear]);

  useEffect(() => {
    if (!mediaScreen) {
      closeSort();
    }
  }, [mediaScreen]);

  const shortingOnMobile = (value: string) => {
    refine(value);
    const selectedOption = options.find(
      (option: any) => option.optionval === value
    );
    const selectedLabel = selectedOption ? selectedOption.optionname : "";
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("sort", selectedLabel);
    router.push(`?${newParams.toString()}`, { scroll: false });
    if (setSortOption) {
      setSortOption(selectedLabel);
    }
    closeSort();
    dispatch(setSortDefault(null));
    dispatch(
      setFilterData({
        filter: "sort",
        filterName: selectedOption.optionname,
        filterValue: selectedOption.optionname,
      })
    );
  };

  const [selectedSort, setSelectedSort] = useState<string | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const sortFromQuery = searchParams.get("sort");
    const currentTab =
      (searchParams.get(
        SEARCH_RESULT_TAB_QUERY_PARAM_KEY
      ) as GLOBAL_SEARCH_RESULTS_SECTIONS) ||
      GLOBAL_SEARCH_RESULTS_SECTIONS.PRODUCT;

    if (sortFromQuery && (pathname !== "/" || currentTab === type)) {
      const normalizedSortFromQuery = normalizeString(sortFromQuery);
      const selectedSortOption = options.find(
        (option: any) =>
          normalizeString(option.optionname) === normalizedSortFromQuery
      );
      if (!selectedSortOption && (type == "recipes" || type == "articles")) {
        setSelectedSort(null);
        dispatch(setSortDefault(sortOptionLabel));
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("sort");
        router.push(`?${newParams.toString()}`, { scroll: false });
        return;
      }
      const selectedSortValue = selectedSortOption
        ? selectedSortOption.optionval
        : pathname === "/"
          ? options.find(
              (option: { optionname: string; optionval: string }) =>
                option.optionname === DEFAULT_SEARCH_PAGE_SORTING
            )?.optionval || options[0].optionval
          : options[0].optionval;
      refine(selectedSortValue);
      setSelectedSort(selectedSortValue);
      if (setSortOption) {
        setSortOption(selectedSortOption.optionname);
      }
    } else if (type == "recipes") {
      refine(
        pathname === "/"
          ? algoliaConfig.indexNames.recipes
          : algoliaConfig.indexNames.recipeSort.newestDate
      );
      setSelectedSort(null);
      dispatch(setSortDefault(sortOptionLabel));
    }
  }, [searchParams, refine]);

  const handleSortChange = (value: string) => {
    refine(value);
    setSelectedSort(value);

    const selectedOption = options.find(
      (option: any) => option.optionval === value
    );
    const selectedLabel = selectedOption ? selectedOption.optionname : "";

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("sort", selectedLabel);
    // newParams.set("sort", value);
    router.push(`?${newParams.toString()}`, { scroll: false });

    dispatch(setSortDefault(null));
    dispatch(
      setFilterData({
        filter: "sort",
        filterName: selectedOption.optionname,
        filterValue: selectedOption.optionname,
      })
    );
    if (type == "products") {
      dispatch(setFavouriteMessage());
      dispatch(setShoppingMessage());
    }
    closeSort();
  };

  return (
    <>
      {/* Mobile Sorting Sidebar */}
      {mediaScreen && (
        <>
          {/* Background Overlay */}
          <div
            className={`fixed top-0 left-0 w-full h-full bg-brandBlack bg-opacity-50 z-[999999999] transition-all duration-700 ease-in-out ${
              isSortVisible ? "opacity-60 visible" : "opacity-0 invisible"
            }`}
          ></div>

          {/* Mobile Sorting Sidebar */}
          <Text
            as="div"
            role="complementary"
            className={`flex flex-col fixed bg-brandWhite right-0 w-full overflow-auto z-[999999999] transition-all duration-700 ease-in-out ${
              isSortVisible ? "bottom-0 visible" : "bottom-[-100%] invisible"
            }`}
          >
            <Button
              className="absolute right-[24px] top-[25px] flex cursor-pointer text-grey950 p-0 bg-transparent hover:bg-transparent focus:bg-transparent border-0"
              onClick={closeSort}
              aria-label="Close sort options"
              variant="icon"
              size="none"
            >
              <span className="icon-close text-body"></span>
            </Button>
            <Box
              as="h1"
              className="font-bold p-6 border-b border-grey350 text-[16px] capitalize"
            >
              {aemFilterData?.sort ?? "Sort"}
            </Box>
            <Text as="ul" className="p-6">
              {options.map((option: any, i: number) => {
                if (option.optionval)
                  return (
                    <Text as="li" key={option.optionval + i}>
                      <label className="flex items-center gap-[10px] text-[14px]">
                        <Box
                          as="div"
                          className="w-12 h-12 flex items-center justify-center"
                        >
                          <Radio
                            name="sortOrder"
                            // defaultChecked={
                            //   options[i].optionval === currentRefinement
                            // }
                            checked={options[i].optionval === currentRefinement}
                            onClick={() => {
                              shortingOnMobile(options[i].optionval);
                            }}
                            className="h-[20px] w-[20px] after:w-[10px] after:h-[10px] before:border-grey900 checked:before:border-grey900 checked:after:bg-grey900"
                          />
                        </Box>
                        <Box as="span">{option.optionname}</Box>
                      </label>
                    </Text>
                  );
              })}
            </Text>
          </Text>
        </>
      )}

      {/* Desktop Sorting Sidebar */}
      {!mediaScreen && (
        <Box as="div" role="complementary" className="flex flex-col gap-4">
          <Box as="div" className="font-bold text-base capitalize">
            {aemFilterData?.sort ?? "Sort"}
          </Box>
          <Box as="div" id="recipe_tour_1">
            <Select
              selectAriaLabel={
                type === "products"
                  ? "Sort products"
                  : type === "articles"
                  ? "Sort articles"
                  : pathname === "/"
                  ? "Sort homepage results"
                  : "Sort recipes"
              }
              options={options}
              onChange={handleSortChange}
              disabled={
                type === "products"
                  ? displayedOffers
                    ? displayedOffers.length <= 0
                    : hitsItems.length <= 0
                  : false
              }
              defaultLabel={
                pathname === "/"
                  ? DEFAULT_SEARCH_PAGE_SORTING
                  : type === "products"
                    ? "Select Sorting..."
                    : type === "articles"
                      ? options[2]?.optionname
                      : options[2]?.optionname
              }
              defaultValue={selectedSort ?? ""}
              className="[&>button]:text-grey900 [&>button]:rounded-[4px] [&>button]:pl-4 [&>button]:h-[56px] [&>button]:py-3 [&>button]:pr-12 [&>button>span]:text-base [&>ul]:!shadow-[0_4px_30px_0px_rgba(0,0,0,0.2)]"
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default Sort;
